import {
  type EpocusProbeAssociation,
  type EpocusProbeAssociationQueryConstructor,
  RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME,
  useRxCollectionEpocusProbeAssociation,
} from "$global-providers/rxdb-provider/collections/epocus-probe-association";
import { useUser } from "$global-providers/user-provider/provider";
import { nanoid } from "nanoid";
import { useCallback, useMemo, useState } from "react";
import { useRxData } from "rxdb-hooks";
import { useActiveProgramId } from "./use-active-program";

/**
 * The backend stores the serial verbatim but matches on trimmed values, so a
 * serial saved with surrounding whitespace would persist happily and then never
 * match an incoming study. Every write path funnels through this.
 */
export const normaliseProbeSerial = (serial: string): string => serial.trim();

export const useMyProbeAssociations = () => {
  const user = useUser();

  const query = useMemo<EpocusProbeAssociationQueryConstructor>(
    () => (collection) =>
      collection?.find({
        selector: { performerId: user.id },
        sort: [{ deviceSerialNumber: "asc" }],
      }),
    [user.id],
  );

  const { result, isFetching } = useRxData<EpocusProbeAssociation>(
    RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME,
    query,
  );

  return { associations: result, loading: isFetching };
};

/**
 * Every probe association in scope, not just the current user's. The To Document
 * queue needs these when serial-restricted triage is off, to tell a scan from a
 * colleague's probe (theirs to claim) apart from one no registered probe
 * accounts for (anyone's to claim).
 */
export const useAllProbeAssociations = () => {
  const query = useMemo<EpocusProbeAssociationQueryConstructor>(
    () => (collection) => collection?.find(),
    [],
  );

  const { result, isFetching } = useRxData<EpocusProbeAssociation>(
    RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME,
    query,
  );

  return { associations: result, loading: isFetching };
};

/** The reason a write was refused, or `null` when it succeeded. */
export type ProbeAssociationFailure =
  | "empty"
  | "duplicate"
  | "unavailable"
  | "failed";

export const PROBE_ASSOCIATION_FAILURE_MESSAGES: Record<
  ProbeAssociationFailure,
  string
> = {
  empty: "Enter a probe serial number.",
  duplicate: "You've already linked that probe serial number.",
  unavailable: "Local database not ready. Please try again.",
  failed: "Couldn't save that probe. Please try again.",
};

export const PROBE_SERIAL_HINT =
  "The serial number is usually printed on the body of the probe. It may also be shown in the probe's companion mobile app.";

export const useManageProbeAssociations = () => {
  const user = useUser();
  const collection = useRxCollectionEpocusProbeAssociation();
  const activeProgramId = useActiveProgramId();
  const [pendingSerial, setPendingSerial] = useState<string | null>(null);

  const add = useCallback(
    async (rawSerial: string): Promise<ProbeAssociationFailure | null> => {
      const deviceSerialNumber = normaliseProbeSerial(rawSerial);
      if (!deviceSerialNumber) return "empty";
      if (!collection) return "unavailable";

      setPendingSerial(deviceSerialNumber);
      try {
        const existing = await collection
          .findOne({ selector: { performerId: user.id, deviceSerialNumber } })
          .exec();
        if (existing) return "duplicate";

        const now = new Date().toISOString();
        await collection.insert({
          id: nanoid(),
          deviceSerialNumber,
          performerId: user.id,
          performerName: `${user.firstName} ${user.lastName}`.trim(),
          programId: activeProgramId,
          meta: {
            createdAt: now,
            updatedAt: now,
            createdByUserId: user.id,
            updatedByUserId: user.id,
          },
        });
        return null;
      } catch (error) {
        console.error("Failed to add probe association", {
          errorName: error instanceof Error ? error.name : typeof error,
        });
        return "failed";
      } finally {
        setPendingSerial(null);
      }
    },
    [collection, user.id, user.firstName, user.lastName, activeProgramId],
  );

  const update = useCallback(
    async (
      id: string,
      rawSerial: string,
    ): Promise<ProbeAssociationFailure | null> => {
      const deviceSerialNumber = normaliseProbeSerial(rawSerial);
      if (!deviceSerialNumber) return "empty";
      if (!collection) return "unavailable";

      setPendingSerial(deviceSerialNumber);
      try {
        const clash = await collection
          .findOne({ selector: { performerId: user.id, deviceSerialNumber } })
          .exec();
        if (clash && clash.id !== id) return "duplicate";

        const doc = await collection.findOne(id).exec();
        if (!doc) return "failed";

        await doc.patch({
          deviceSerialNumber,
          meta: {
            ...doc.toJSON().meta,
            updatedAt: new Date().toISOString(),
            updatedByUserId: user.id,
          },
        });
        return null;
      } catch (error) {
        console.error("Failed to update probe association", {
          errorName: error instanceof Error ? error.name : typeof error,
        });
        return "failed";
      } finally {
        setPendingSerial(null);
      }
    },
    [collection, user.id],
  );

  const remove = useCallback(
    async (id: string): Promise<ProbeAssociationFailure | null> => {
      if (!collection) return "unavailable";
      try {
        const doc = await collection.findOne(id).exec();
        await doc?.remove();
        return null;
      } catch (error) {
        console.error("Failed to remove probe association", {
          errorName: error instanceof Error ? error.name : typeof error,
        });
        return "failed";
      }
    },
    [collection],
  );

  return { add, update, remove, pendingSerial };
};
